import Icon from "../ds/Icon";

// --- UTILITY SELECTOR (v3.1 proximity fallback) ------------------------------
// Two render modes via the `variant` prop:
//
//   variant="inline" (default in the report view)
//     A compact "Other utilities in your area" switcher that lives INSIDE a
//     rendered report, right under the violation-summary card. The most-likely
//     utility is already shown as the report above it, so this list is the
//     remaining candidates (caller filters out the active pwsid). Tapping one
//     swaps the report in place. EWG pattern: served utility on top, others below.
//
//   variant="full"
//     The original standalone picker. Used only as a fallback when the initial
//     auto-load of the most-likely utility fails, so the user can pick manually.
//
// Design contract:
//  - Zero anchor (<a>) tags by intent. Everything here is a <button>. This keeps
//    the picker immune to the iPad-Safari paste pattern that drops <a opening tags.
//  - Reuses the global @keyframes spin defined in TrustButVerify's <style> block.
//  - No localStorage, no em/en dashes, brand palette only.
//
// Props:
//  - variant:        "inline" | "full"  (default "full")
//  - userLocation:   { city, state, county } | null   (full variant header only)
//  - candidates:     [{ pwsid, name, city, state, population_served, distance_miles, source }]
//  - selectingPwsid: pwsid currently resolving (drives per-card spinner) | null
//  - errorMessage:   inline error string after a failed resolve | ""
//  - onSelect:       (pwsid) => void
//  - onBack:         () => void   (full variant only)
export default function UtilitySelector({
  variant = "full",
  userLocation = null,
  candidates = [],
  selectingPwsid = null,
  errorMessage = "",
  onSelect,
  onBack,
}) {
  const inline = variant === "inline";

  // Largest population first. distance_miles is unreliable in dense metros
  // (ZIP centroid to registered utility address, often 0.0), so population
  // served is the primary ordering heuristic.
  const sorted = [...candidates].sort(
    (a, b) => (b.population_served || 0) - (a.population_served || 0)
  );

  // Inline switcher with nothing to switch to renders nothing.
  if (inline && sorted.length === 0) return null;

  const isBusy = selectingPwsid != null;

  function fmtPop(n) {
    if (n == null || isNaN(n)) return null;
    return Number(n).toLocaleString("en-US");
  }

  // Shared candidate row. `top` (full variant, first card) gets the accent
  // treatment + "MOST LIKELY" pill. Inline cards never get it (the most-likely
  // utility is the report shown above the switcher).
  function CandidateCard({ c, i, top }) {
    const loading = selectingPwsid === c.pwsid;
    const disabled = isBusy && !loading;
    const pop = fmtPop(c.population_served);
    const showDistance =
      typeof c.distance_miles === "number" && c.distance_miles > 0;
    const cityState = [c.city, c.state].filter(Boolean).join(", ");

    return (
      <button
        key={c.pwsid || i}
        type="button"
        onClick={() => onSelect && onSelect(c.pwsid)}
        disabled={disabled || loading}
        data-testid={`utility-candidate-${c.pwsid || i}`}
        className="card-default w-full text-left cursor-pointer"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: inline ? "12px 14px" : "14px 14px",
          minHeight: inline ? 62 : 72,
          border: top
            ? "2px solid #51B0E6"
            : "1px solid rgba(15, 20, 25, 0.06)",
          background: top ? "rgba(81, 176, 230, 0.04)" : "#FFFFFF",
          opacity: disabled ? 0.5 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {(top || c.source) && (
            <div className="flex items-center gap-1.5 flex-wrap" style={{ marginBottom: 5 }}>
              {top && (
                <span
                  className="rounded-pill font-bold uppercase tracking-wider"
                  style={{ fontSize: 9, padding: "2px 8px", background: "rgba(81, 176, 230, 0.12)", color: "#1F6FA0" }}
                >
                  MOST LIKELY
                </span>
              )}
              {c.source && (
                <span
                  className="rounded-pill font-semibold uppercase tracking-wider"
                  style={{ fontSize: 9, padding: "2px 8px", background: "#F0F1F3", color: "#6E7174" }}
                >
                  {c.source}
                </span>
              )}
            </div>
          )}
          <div className="font-semibold" style={{ fontSize: 13, lineHeight: 1.3, color: "#0F1419" }}>
            {c.name}
          </div>
          <div style={{ fontSize: 11, marginTop: 3, color: "#6E7174", lineHeight: 1.4 }}>
            {cityState}
            {pop ? `${cityState ? " . " : ""}Serves ${pop}` : ""}
            {showDistance ? ` . ${c.distance_miles.toFixed(1)} mi` : ""}
          </div>
        </div>

        {loading ? (
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid #51B0E6",
              borderTopColor: "transparent",
              animation: "spin 1s linear infinite",
              display: "inline-block",
              flexShrink: 0,
            }}
            aria-label="Loading"
          />
        ) : (
          <span style={{ flexShrink: 0, display: "inline-flex", color: "#A6A8AB" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
        )}
      </button>
    );
  }

  const errorCard = errorMessage ? (
    <div
      className="card-critical flex items-start gap-2.5"
      style={{ padding: "11px 14px", marginBottom: inline ? 10 : 14 }}
      data-testid="utility-select-error"
    >
      <Icon name="hazard" size={18} color="#B84A4A" />
      <div style={{ fontSize: 12, lineHeight: 1.5, color: "#3D4043" }}>{errorMessage}</div>
    </div>
  ) : null;

  // ---- INLINE SWITCHER ------------------------------------------------------
  if (inline) {
    return (
      <div className="mb-3" data-testid="utility-switcher">
        <div
          className="text-micro uppercase tracking-widest font-semibold mb-2"
          style={{ fontSize: 10, color: "#6E7174" }}
        >
          OTHER UTILITIES IN YOUR AREA
        </div>
        {errorCard}
        <div className="flex flex-col gap-2" data-testid="utility-candidates">
          {sorted.map((c, i) => (
            <CandidateCard key={c.pwsid || i} c={c} i={i} top={false} />
          ))}
        </div>
        <div style={{ fontSize: 11, marginTop: 8, color: "#A6A8AB", lineHeight: 1.4 }}>
          Tap a utility to view its report.
        </div>
      </div>
    );
  }

  // ---- FULL STANDALONE PICKER (error fallback) ------------------------------
  const locLabel = userLocation
    ? [userLocation.city, userLocation.state].filter(Boolean).join(", ")
    : "your area";

  return (
    <div className="mx-auto" style={{ maxWidth: 420, margin: "24px auto", padding: "0 16px" }} data-testid="utility-selector">
      <div className="text-center" style={{ marginBottom: 18 }}>
        <div
          className="rounded-full flex items-center justify-center mx-auto"
          style={{ width: 56, height: 56, background: "rgba(81, 176, 230, 0.10)", border: "2px solid rgba(81, 176, 230, 0.28)", marginBottom: 14 }}
        >
          <Icon name="pin" size={26} color="#51B0E6" />
        </div>
        <h2 className="font-display font-semibold" style={{ fontSize: 21, lineHeight: 1.2, marginBottom: 6, color: "#0F1419" }}>
          We found {sorted.length} {sorted.length === 1 ? "utility" : "utilities"} near {locLabel}
        </h2>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: "#3D4043" }}>
          Pick the one that supplies your home. The most likely match is listed first.
        </p>
      </div>

      {errorCard}

      <div className="flex flex-col gap-2" style={{ marginBottom: 18 }} data-testid="utility-candidates">
        {sorted.map((c, i) => (
          <CandidateCard key={c.pwsid || i} c={c} i={i} top={i === 0} />
        ))}
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={() => onBack && onBack()}
          disabled={isBusy}
          data-testid="utility-select-back"
          className="font-semibold rounded-card cursor-pointer"
          style={{ padding: "10px 18px", fontSize: 12, background: "transparent", border: "1px solid rgba(15, 20, 25, 0.08)", color: "#6E7174", opacity: isBusy ? 0.5 : 1 }}
        >
          Search a different location
        </button>
      </div>
    </div>
  );
}
